import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef } from "react";
import MenuComponent from "shared/components/common/MenuComponent";
import DotsVerticalIcon from "shared/components/icons/dot/DotsVerticalIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { useWorkflowInstancePossibleActions } from "shared/helpers/workflow/useWorkflowInstancePossibleActions";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";

export type PillarTableColumnWorkflowInstancePossibleActionsProps<T> =
  PillarTableBaseColumnCellValuePathProps<T> & ComponentPropsWithoutRef<"div">;

const PillarTableColumnWorkflowInstancePossibleActions = <T,>({
  cellValuePath,
}: PillarTableColumnWorkflowInstancePossibleActionsProps<T>) => {
  const rowContext = usePillarTableRowContext();
  const tableContext = usePillarTableContext();

  const workflowInstance: WorkflowInstanceView =
    lodashGet(rowContext?.rowValue, cellValuePath ?? "workflowInstance") ??
    rowContext.rowValue;

  const { menuItems, WorkflowInstanceModalComponents } =
    useWorkflowInstancePossibleActions({
      workflowInstance,
      testid: tableContext.testidTable,
      refetchCallback: async () => {
        await tableContext?.refetchData?.();
      },
    });

  if (!workflowInstance) {
    return <></>;
  }

  if (!menuItems.length) {
    return <></>;
  }

  const [firstMenuItem, ...restMenuItems] = menuItems;

  return (
    <>
      <WorkflowInstanceModalComponents />
      <div className="flex item-center gap-x-1">
        <PillarForm.Button
          title={firstMenuItem.label}
          className={firstMenuItem.itemClassName}
          icon={firstMenuItem.icon as React.ReactElement}
          onClick={firstMenuItem.onSelect}
        >
          {firstMenuItem.label}
        </PillarForm.Button>

        {!!restMenuItems.length && (
          <MenuComponent
            menuItems={restMenuItems}
            overrideClassName="flex"
            wrapperClassName="!inline-flex"
            buttonIcon={
              <DotsVerticalIcon className="text-neutral-mid-550 w-2 h-2" />
            }
          />
        )}
      </div>
    </>
  );
};

PillarTableColumnWorkflowInstancePossibleActions.doNotExport = true;
export default PillarTableColumnWorkflowInstancePossibleActions;
